import type { FormDoc } from "@koaform/form-schema";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  api,
  clearWip,
  isRevisionConflict,
  rememberDraft,
  writeWip,
  type Draft,
} from "../lib/api.js";

export type SaveState =
  | { status: "clean" }
  | { status: "saving" }
  | { status: "saved"; at: number }
  | { status: "conflict"; server: FormDoc; revision: number }
  | { status: "error"; message: string };

const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * Owns the draft document and its persistence.
 *
 * Two behaviours here are product decisions, not implementation detail:
 *
 * 1. LAZY CREATION. Nothing is persisted until the form has real content —
 *    a changed title or a first field. Opening the builder, or a crawler
 *    hitting it, creates zero rows.
 *
 * 2. NO SILENT MERGE. If another tab saved first, the server 409s and we
 *    surface a conflict rather than overwriting. Merging two field arrays
 *    would resurrect deleted fields and cannot be explained to a user.
 */
export const useDraft = (initial: FormDoc, existing?: Draft) => {
  const [doc, setDoc] = useState<FormDoc>(existing?.schema ?? initial);
  const [draftId, setDraftId] = useState<string | null>(existing?.id ?? null);
  const [save, setSave] = useState<SaveState>({ status: "clean" });
  const [resumeToken, setResumeToken] = useState<string | null>(null);

  const revision = useRef(existing?.revision ?? 0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<FormDoc | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  // The id as soon as it exists, without waiting for a state re-render —
  // publish needs it in the same tick it triggers a save.
  const draftIdRef = useRef<string | null>(existing?.id ?? null);

  /** "Real content" is the trigger for first persistence. */
  const hasContent = useCallback(
    (d: FormDoc) => d.fields.length > 0 || d.title !== initial.title,
    [initial.title],
  );

  const flush = useCallback(async (): Promise<void> => {
    const next = pending.current;
    if (!next || inFlight.current) return;
    if (!draftIdRef.current && !hasContent(next)) return;

    pending.current = null;
    setSave({ status: "saving" });

    const run = (async () => {
      try {
        if (!draftIdRef.current) {
          const created = await api.createDraft(next);
          draftIdRef.current = created.id;
          setDraftId(created.id);
          revision.current = created.revision;
          if (created.resumeToken) setResumeToken(created.resumeToken);
          rememberDraft({
            id: created.id,
            title: created.title,
            updatedAt: created.updatedAt,
            resumeToken: created.resumeToken,
          });
        } else {
          const saved = await api.saveDraft(
            draftIdRef.current,
            next,
            revision.current,
          );
          revision.current = saved.revision;
          rememberDraft({
            id: saved.id,
            title: saved.title,
            updatedAt: saved.updatedAt,
          });
        }
        setSave({ status: "saved", at: Date.now() });
        // The server holds this state now; the mirror has done its job.
        if (!pending.current) clearWip();
      } catch (err) {
        if (isRevisionConflict(err)) {
          // The 409 carries the winning document and its revision, so
          // resolving needs no extra round trip.
          const winner = err.details as Draft | undefined;
          setSave(
            winner
              ? {
                  status: "conflict",
                  server: winner.schema,
                  revision: winner.revision,
                }
              : { status: "error", message: err.message },
          );
        } else {
          setSave({
            status: "error",
            message: err instanceof Error ? err.message : "Could not save.",
          });
        }
      }
    })();

    inFlight.current = run;
    await run;
    inFlight.current = null;
    // A change that landed mid-request still needs writing.
    if (pending.current) await flush();
  }, [hasContent]);

  const update = useCallback(
    (next: FormDoc) => {
      setDoc(next);
      pending.current = next;
      // Mirror every edit to localStorage so a refresh inside the debounce
      // window — or before the first save even created a draft — loses nothing.
      writeWip({ draftId: draftIdRef.current, doc: next, at: Date.now() });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), AUTOSAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  /**
   * Persists a draft that arrived with content already in it — a template.
   *
   * Autosave only fires on `update()`, so without this a form seeded from
   * ?template= sat entirely unsaved: Publish had no draft id and silently did
   * nothing, and closing the tab lost the lot.
   */
  useEffect(() => {
    if (draftIdRef.current || !hasContent(doc)) return;
    pending.current = doc;
    void flush();
    // Mount-only on purpose: every later change goes through `update`, which
    // schedules its own save. Re-running this would double-write.
  }, []);

  /**
   * Writes anything outstanding and resolves with the draft id.
   *
   * Publish calls this first, so pressing it a moment after typing cannot act
   * on a draft the server has never seen.
   */
  const ensureSaved = useCallback(async (): Promise<string | null> => {
    if (timer.current) clearTimeout(timer.current);
    if (pending.current) await flush();
    else if (inFlight.current) await inFlight.current;
    return draftIdRef.current;
  }, [flush]);

  /**
   * Closing the tab mid-debounce would otherwise lose up to 1.5s of edits.
   * `pagehide` fires when mobile Safari backgrounds a tab, where `beforeunload`
   * does not.
   */
  useEffect(() => {
    const onHide = () => {
      if (pending.current) void flush();
    };
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [flush]);

  /** Take the server's document, discarding this tab's divergent edits. */
  const resolveConflict = useCallback((server: FormDoc, rev: number) => {
    pending.current = null;
    revision.current = rev;
    setDoc(server);
    setSave({ status: "clean" });
  }, []);

  return {
    doc,
    update,
    draftId,
    save,
    resumeToken,
    resolveConflict,
    ensureSaved,
  };
};
